// CompaniesListPage — список компаний с поиском + пагинацией + FAB.
// R33c / U-6: живой поиск — param `q` (backend R33b), debounce 300 мс.
// R63 / U-34-D: поиск с флажками полей (SearchFieldBar → `fields` CSV; industry — только в
// дефолтном поиске «все поля», флажок не показываем — SSOT §3.3 + §8 п.2).
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Fab,
  TablePagination,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { companiesApi, type Company } from '@/api/companies';
import { useUserMap, resolveUserName } from '@/api/users';
import { SearchFieldBar, type SearchFieldOption, type SearchFieldValue } from '@/components/SearchFieldBar';
import { formatDate } from '@/lib/format';
import { useTableSort } from '@/hooks/useTableSort';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Поля сортировки компаний (U-23: whitelist backend createdAt|updatedAt|name). */
type CompanySortField = 'name' | 'createdAt';

/** Направление сортировки. */
type SortDirection = 'asc' | 'desc';

/** Состояние сортировки: default createdAt,desc (backend default created_at DESC). */
interface CompanySort {
  field: CompanySortField;
  direction: SortDirection;
}

const DEFAULT_SORT: CompanySort = { field: 'createdAt', direction: 'desc' };

/** Флажки полей поиска компаний (SSOT §3.3; ИНН = tax_id на проводе). */
function buildCompanyFieldOptions(): SearchFieldOption[] {
  return [
    { key: 'name', label: t('search.field.name') },
    { key: 'website', label: t('search.field.website') },
    { key: 'site_name', label: t('search.field.site_name') },
    { key: 'email', label: t('search.field.email') },
    { key: 'phone', label: t('search.field.phone') },
    { key: 'tax_id', label: t('search.field.tax_id') },
    { key: 'license_number', label: t('search.field.license_number') },
    { key: 'ip_address', label: t('search.field.ip_address') },
  ];
}

/** Страница списка компаний. */
export function CompaniesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  // R63: q + fields в одном состоянии (SearchFieldBar — единая панель).
  const [searchValue, setSearchValue] = useState<SearchFieldValue>({ q: '', fields: [] });
  // U-23: состояние сортировки (канон DealsListPage: новый field → asc, активный → toggle).
  const { sort, handleSortClick } = useTableSort<CompanySortField>(DEFAULT_SORT, setPage);

  const query = useQuery({
    queryKey: ['companies', page, searchValue.q, searchValue.fields.join(','), sort],
    queryFn: () =>
      companiesApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(searchValue.q ? { q: searchValue.q } : {}),
        ...(searchValue.fields.length > 0 ? { fields: searchValue.fields.join(',') } : {}),
        sort: `${sort.field},${sort.direction}`,
      }),
  });

  // R23 / баг D21: резолвим только владельцев видимых строк (ids-режим /api/users?ids=).
  const ownerIds = useMemo(
    () =>
      (query.data?.content ?? [])
        .map((company) => company.ownerUserId)
        .filter((id): id is string => Boolean(id)),
    [query.data],
  );
  const userMap = useUserMap(ownerIds);

  return (
    <Box>
      <PageHeader title="Компании" subtitle="База компаний CRM" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <SearchFieldBar
          fieldsOptions={buildCompanyFieldOptions()}
          value={searchValue}
          onChange={(next) => {
            setPage(0);
            setSearchValue(next);
          }}
          placeholder={t('search.placeholder')}
          searchAriaLabel="Поиск компаний"
        />
      </Stack>
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка компаний…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить компании"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title="Нет компаний"
          description="Создайте первую компанию"
          actionLabel="Создать компанию"
          onAction={() => navigate('/crm/companies/new')}
        />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sort.field === 'name' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'name'}
                      direction={sort.field === 'name' ? sort.direction : 'asc'}
                      onClick={() => handleSortClick('name')}
                    >
                      Название
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>ИНН</TableCell>
                  <TableCell>Отрасль</TableCell>
                  <TableCell>Владелец</TableCell>
                  {/* U-23: сортируемая «Создана» (createdAt). */}
                  <TableCell sortDirection={sort.field === 'createdAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'createdAt'}
                      direction={sort.field === 'createdAt' ? sort.direction : 'desc'}
                      onClick={() => handleSortClick('createdAt')}
                    >
                      {t('crm.companies.list.createdAt')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((c: Company) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/crm/companies/${c.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.taxId ?? '—'}</TableCell>
                    <TableCell>{c.industry ?? '—'}</TableCell>
                    <TableCell>{resolveUserName(userMap, c.ownerUserId)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(c.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          component={RouterLink}
          to="/crm/companies/new"
          color="primary"
          aria-label="Создать компанию"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>
      </Stack>
    </Box>
  );
}
